import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { MarketingNav, MarketingFooter } from "@/components/MarketingShell";
import { useSite } from "@/lib/site";
import api from "@/lib/api";
import "./demo.css";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] },
};

const FAQ = [
  { q: "Can I switch plans anytime?", a: "Yes. Move between personal and team plans whenever you like — upgrades apply immediately, and moving to a team plan makes you the team admin with seats to invite your people." },
  { q: "What are tokens?", a: "Tokens are the AI fuel your agent uses to read, draft and answer. Every plan includes a monthly pool that resets each period, and you can top up with token packs if a busy month runs it down." },
  { q: "How do team seats work?", a: "Like Google Workspace: the team admin pays once, gets a number of seats, and invites members by email. Every member gets their own full agent — memory, rules, briefs and WhatsApp." },
  { q: "Do I need anything special for WhatsApp?", a: "Just your phone number. Link it from Settings in under a minute — a verification code arrives on WhatsApp, and from then on your agent lives in that chat. Unlink instantly anytime." },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [audTab, setAudTab] = useState("personal");
  const audienceOf = (p) => p.audience || (p.seats <= 1 ? "personal" : "team");
  const shown = packages.filter((p) => audienceOf(p) === audTab);
  const { g, seo } = useSite("pricing");

  useEffect(() => {
    document.title = seo?.meta_title || "Pricing — Agentic Inbox";
    window.scrollTo(0, 0);
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, [seo]);

  const choosePackage = (pkg) => {
    const token = localStorage.getItem("agi_token");
    if (token) { navigate("/billing"); return; }
    localStorage.setItem("agi_pref_plan", pkg.id);
    navigate(`/login?package=${pkg.id}`);
  };

  const gridCols = shown.length >= 3 ? "md:grid-cols-3" : shown.length === 2 ? "md:grid-cols-2 max-w-3xl mx-auto" : "md:grid-cols-1 max-w-md mx-auto";

  return (
    <div className="demo-root min-h-screen" data-testid="pricing-page">
      <MarketingNav active="/pricing" />

      {/* HERO */}
      <section className="relative demo-grain pt-40 pb-14 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#86868B] mb-4">{g("hero_label", "Pricing")}</p>
            <h1 className="text-5xl md:text-7xl font-semibold tracking-tighter leading-[1.05] text-balance">
              {g("hero_title", "Priced like a tool. Pays back like a hire.")}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[#86868B] leading-relaxed max-w-2xl mx-auto">
              {g("hero_sub", "Start solo or bring your whole team. Switch anytime — the agent comes with you.")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* TOGGLE + PLANS */}
      <section className="px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <motion.div {...fadeUp} className="flex justify-center mb-12">
            <div className="inline-flex rounded-full border border-black/10 bg-white p-1" data-testid="pricing-tabs">
              <button data-testid="pricing-tab-personal" onClick={() => setAudTab("personal")}
                className={`demo-pill px-6 py-2 text-[13px] font-semibold ${audTab === "personal" ? "bg-[#0071E3] text-white" : "text-[#1d1d1f]/60 hover:text-[#1d1d1f]"}`}>{g("tab_me", "For me")}</button>
              <button data-testid="pricing-tab-team" onClick={() => setAudTab("team")}
                className={`demo-pill px-6 py-2 text-[13px] font-semibold ${audTab === "team" ? "bg-[#0071E3] text-white" : "text-[#1d1d1f]/60 hover:text-[#1d1d1f]"}`}>{g("tab_team", "For my team")}</button>
            </div>
          </motion.div>

          <div className={`grid grid-cols-1 gap-6 items-stretch ${gridCols}`} data-testid="pricing-grid">
            {shown.map((pkg, i) => {
              const slug = pkg.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <motion.div key={pkg.id} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.07 }}
                  data-testid={`pricing-card-${slug}`}
                  className={`demo-card relative p-9 flex flex-col ${pkg.popular ? "ring-2 ring-[#0071E3]" : ""}`}>
                  {pkg.popular && (
                    <span className="absolute -top-3 left-8 bg-[#0071E3] text-white text-[11px] font-bold rounded-full px-3 py-1">{g("badge_popular", "Most popular")}</span>
                  )}
                  <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-[#86868B]">{pkg.name}</p>
                  <div className="mt-3 flex items-baseline gap-1.5">
                    <span data-testid={`pricing-price-${slug}`} className="demo-heading text-4xl font-semibold tracking-tighter text-[#1d1d1f]">
                      ${pkg.price_monthly % 1 === 0 ? pkg.price_monthly.toFixed(0) : pkg.price_monthly}
                    </span>
                    <span className="text-[13px] text-[#86868B]">{g("per_month", "/month")} · {pkg.seats} {pkg.seats > 1 ? g("seat_many", "seats") : g("seat_one", "seat")}</span>
                  </div>
                  <ul className="space-y-3 flex-1 mt-7 mb-8">
                    {(pkg.features || []).map((feat) => (
                      <li key={feat} className="flex items-start gap-2.5 text-[15px] text-[#1d1d1f]/85">
                        <Check size={15} strokeWidth={2.5} className="text-[#0071E3] mt-0.5 shrink-0" /> {feat}
                      </li>
                    ))}
                  </ul>
                  <button data-testid={`pricing-cta-${slug}`} onClick={() => choosePackage(pkg)}
                    className={`demo-pill w-full py-3 text-[14px] font-semibold ${pkg.popular ? "bg-[#0071E3] hover:bg-[#0077ED] text-white" : "bg-white border border-black/10 text-[#1d1d1f] hover:bg-black/[0.03]"}`}>
                    {pkg.price_monthly <= 0 ? g("cta_start_free", "Start free") : `${g("cta_get", "Get")} ${pkg.name}`}
                  </button>
                </motion.div>
              );
            })}
            {packages.length === 0 && <p className="text-sm text-[#86868B] text-center md:col-span-3">{g("no_packages", "No packages published yet.")}</p>}
            {packages.length > 0 && shown.length === 0 && (
              <p data-testid="pricing-empty-tab" className="text-sm text-[#86868B] text-center md:col-span-3">
                No {audTab === "personal" ? "personal" : "team"} plans published yet —{" "}
                <button onClick={() => setAudTab(audTab === "personal" ? "team" : "personal")} className="text-[#0071E3] underline">
                  check out the {audTab === "personal" ? "team" : "personal"} plans →</button>
              </p>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 py-24 bg-white border-y border-black/5">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fadeUp} className="mb-10">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#86868B] mb-3">{g("faq_label", "Questions")}</p>
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight">{g("faq_title", "Before you decide.")}</h2>
          </motion.div>
          <div className="space-y-3" data-testid="pricing-faq">
            {FAQ.map((f, i) => (
              <motion.details key={i} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.05 }}
                className="demo-card group p-0 overflow-hidden" data-testid={`faq-item-${i}`}>
                <summary className="cursor-pointer list-none px-7 py-5 flex items-center justify-between gap-4">
                  <span className="demo-heading text-[16px] font-medium text-[#1d1d1f]">{g(`faq_${i + 1}_q`, f.q)}</span>
                  <span className="text-[#86868B] text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="px-7 pb-6 text-[15px] text-[#86868B] leading-relaxed">{g(`faq_${i + 1}_a`, f.a)}</p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 px-6 bg-[#1d1d1f] demo-grain relative">
        <div className="max-w-3xl mx-auto text-center relative">
          <motion.div {...fadeUp}>
            <h2 className="text-4xl md:text-6xl font-semibold tracking-tighter text-[#F5F5F7] text-balance">
              {g("cta_title", "The cheapest plan still beats doing it by hand.")}
            </h2>
            <p className="mt-5 text-lg text-[#86868B]">{g("cta_sub", "Start free. Upgrade the day it earns it.")}</p>
            <Link to="/login" data-testid="pricing-footer-cta"
              className="demo-pill inline-block mt-9 bg-white text-[#1d1d1f] text-[15px] font-semibold px-9 py-4 hover:bg-[#F5F5F7]">
              {g("cta_btn", "Get Started")}
            </Link>
          </motion.div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
